import React, { useEffect, useState } from "react";
import { api, CAD, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, RotateCcw, FileText, ChevronDown } from "lucide-react";

const ACTIONS = [
  { key: "mark_invoice_sent", label: "Mark Invoice Sent", icon: FileText, tone: "bg-blue-500 hover:bg-blue-600" },
  { key: "mark_commission_received", label: "Mark Commission Received", icon: CheckCircle2, tone: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "mark_invoice_pending", label: "Reset to Invoice Pending", icon: RotateCcw, tone: "bg-amber-500 hover:bg-amber-600" },
];

export default function B2CCommissions() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState({});
  const [action, setAction] = useState(null); // one of ACTIONS
  const [form, setForm] = useState({});
  const [openMenu, setOpenMenu] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v && v !== "all"));
    const [r, m] = await Promise.all([api.get(`/b2c/commission-claims?${qs}`), api.get("/b2c/meta")]);
    setRows(r.data); setMeta(m.data); setSelected({});
  };
  useEffect(() => { load(); }, [filters]); // eslint-disable-line

  if (!meta) return <div>Loading…</div>;

  const selectedIds = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
  const toggleAll = (checked) => { const next = {}; rows.forEach(r => next[r.id] = checked); setSelected(next); };

  const openAction = (a) => {
    setAction(a);
    setForm({});
    setOpenMenu(false);
  };

  const runBulk = async () => {
    if (!action) return;
    let payload = { ...form };
    if (action.key === "mark_invoice_sent") {
      if (!form.invoice_number || !form.invoice_date) return toast.error("Invoice # and date required");
      payload.invoice_amount = form.invoice_amount ? Number(form.invoice_amount) : null;
    }
    if (action.key === "mark_commission_received") {
      if (!form.amount_received) return toast.error("Amount received required");
      payload.amount_received = Number(form.amount_received);
    }
    try {
      const r = await api.post("/b2c/commission-claims/bulk", { ids: selectedIds, action: action.key, payload });
      toast.success(`${r.data.modified} claim${r.data.modified === 1 ? "" : "s"} updated`);
      setAction(null); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Commission Claims</h1>
        <p className="text-sm text-slate-500 mt-1">One claim per enrolled semester. Filter and update multiple claims at once.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200">
        <Select value={filters.college_id || "all"} onValueChange={v => setFilters({ ...filters, college_id: v })}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="claim-college-filter"><SelectValue placeholder="College" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Colleges</SelectItem>{meta.colleges.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.intake_id || "all"} onValueChange={v => setFilters({ ...filters, intake_id: v })}>
          <SelectTrigger className="h-9 w-44 bg-white"><SelectValue placeholder="Intake" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Intakes</SelectItem>{meta.intakes.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(filters.semester_number || "all")} onValueChange={v => setFilters({ ...filters, semester_number: v === "all" ? "" : Number(v) })}>
          <SelectTrigger className="h-9 w-40 bg-white"><SelectValue placeholder="Semester" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Semesters</SelectItem>{[1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>Semester {n}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={v => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="claim-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{["INVOICE PENDING", "INVOICE SENT", "COMMISSION RECEIVED"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2 relative">
          <span className="text-sm text-slate-600">{selectedIds.length} selected</span>
          <Button
            data-testid="bulk-action-btn"
            disabled={selectedIds.length === 0}
            onClick={() => setOpenMenu(o => !o)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            Bulk Action <ChevronDown className="w-4 h-4 ml-1" />
          </Button>
          {openMenu && selectedIds.length > 0 && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOpenMenu(false)} />
              <div className="absolute right-0 top-11 z-30 w-64 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
                {ACTIONS.map(a => (
                  <button
                    key={a.key}
                    data-testid={`bulk-action-${a.key}`}
                    onClick={() => openAction(a)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left"
                  >
                    <a.icon className="w-4 h-4 text-slate-500" /> {a.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3"><Checkbox onCheckedChange={toggleAll} checked={rows.length > 0 && selectedIds.length === rows.length} /></th>
              <th className="text-left px-4 py-3">Student</th>
              <th className="text-left px-4 py-3">College · Program</th>
              <th className="text-left px-4 py-3">Intake</th>
              <th className="text-left px-4 py-3">Sem</th>
              <th className="text-left px-4 py-3">Expected</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">Received</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`claim-row-${c.id.slice(0, 8)}`}>
                <td className="px-3 py-3"><Checkbox checked={!!selected[c.id]} onCheckedChange={v => setSelected({ ...selected, [c.id]: !!v })} /></td>
                <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => nav(`/b2c/applications/${c.application_id}`)}>{c.student?.full_name}<div className="text-xs text-slate-500 mono">{c.application?.application_id}</div></td>
                <td className="px-4 py-3">{c.college?.name}<div className="text-xs text-slate-500">{c.program?.name}</div></td>
                <td className="px-4 py-3">{c.intake?.name}</td>
                <td className="px-4 py-3">{c.semester_number}</td>
                <td className="px-4 py-3 mono">{c.expected_commission_amount != null ? CAD(c.expected_commission_amount) : "—"}</td>
                <td className="px-4 py-3 text-xs">{c.invoice_number || "—"}<div className="text-slate-500">{fmtDate(c.invoice_date)}</div></td>
                <td className="px-4 py-3 mono">{c.amount_received != null ? CAD(c.amount_received) : "—"}</td>
                <td className="px-4 py-3"><span className={`stage-badge ${c.status === "COMMISSION RECEIVED" ? "bg-emerald-50 text-emerald-900 border-emerald-300" : c.status === "INVOICE SENT" ? "bg-blue-50 text-blue-900 border-blue-300" : "bg-amber-50 text-amber-900 border-amber-300"}`}>{c.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500">No commission claims match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setForm({}); } }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{action?.label} · {selectedIds.length} claim{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>All selected claims will be updated with the values below.</DialogDescription>
          </DialogHeader>

          {action?.key === "mark_invoice_sent" && (
            <div className="space-y-3">
              <div><Label>Invoice Number *</Label><Input data-testid="bulk-invoice-number" value={form.invoice_number || ""} onChange={e => setForm({ ...form, invoice_number: e.target.value })} /></div>
              <div><Label>Invoice Date *</Label><Input type="date" data-testid="bulk-invoice-date" value={form.invoice_date || ""} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
              <div><Label>Invoice Amount (optional)</Label><Input type="number" value={form.invoice_amount || ""} onChange={e => setForm({ ...form, invoice_amount: e.target.value })} /></div>
              <p className="text-xs text-slate-500">The same invoice number will be attached to all selected claims.</p>
            </div>
          )}

          {action?.key === "mark_commission_received" && (
            <div className="space-y-3">
              <div><Label>Amount Received (per claim) *</Label><Input type="number" data-testid="bulk-amount-received" value={form.amount_received || ""} onChange={e => setForm({ ...form, amount_received: e.target.value })} /></div>
              <div><Label>Received Date</Label><Input type="date" value={form.received_date || ""} onChange={e => setForm({ ...form, received_date: e.target.value })} /></div>
              <div><Label>Payment Reference (optional)</Label><Input placeholder="Wire ref, cheque #, etc." value={form.payment_reference || ""} onChange={e => setForm({ ...form, payment_reference: e.target.value })} /></div>
              <p className="text-xs text-slate-500">Finance and admin will get an in-portal alert on save.</p>
            </div>
          )}

          {action?.key === "mark_invoice_pending" && (
            <div className="space-y-3">
              <div><Label>Notes (optional)</Label><Input value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Why is this being reset?" /></div>
              <p className="text-xs text-slate-500">Use this to send a batch of claims back to the invoicing team.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAction(null); setForm({}); }}>Cancel</Button>
            <Button data-testid="bulk-confirm" onClick={runBulk} className={`text-white ${action?.tone || "bg-orange-500 hover:bg-orange-600"}`}>Apply to {selectedIds.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
